import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NAV_ITEMS } from "../../data/nav";
import { cn } from "../../lib/cn";
import { Menu, X } from "lucide-react";

const Navbar = () => {
  const [solid, setSolid] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Handle navigation with hash links
  const handleNavClick = (e, href) => {
    if (href.includes('#')) {
      e.preventDefault();
      const [path, hash] = href.split('#');
      
      if (location.pathname === path || path === '/') {
        // Same page, just scroll to element
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Different page, navigate then scroll
        navigate(path);
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
      setMobileMenuOpen(false);
    }
  };

  // Check if link is active
  const isActive = (href) => {
    if (href.includes('#')) {
      return location.pathname === '/' && location.hash === `#${href.split('#')[1]}`;
    }
    return href === location.pathname;
  };

  return (
    <header
      className={cn(
        "fixed top-0 z-50 w-full transition-all duration-300 h-[68px] md:h-[76px] lg:h-[88px]",
        solid
          ? "bg-black/80 backdrop-blur-xl border-b border-white/10"
          : "bg-black/30 backdrop-blur-sm"
      )}
    >
      <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-4 md:px-6 lg:px-10">
        {/* Logo - Left corner, premium placement */}
        <Link 
          to="/" 
          className="flex items-center gap-2 md:gap-3 group shrink-0"
          data-testid="navbar-logo"
        >
          <img
            src="/glowlawns-logo-header.png"
            alt="Glow Lawns"
            className="h-9 md:h-11 lg:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            style={{
              filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.3))",
            }}
          />
          <span className="hidden md:block text-white font-semibold text-sm tracking-wide">
            glowLAWNS
          </span>
        </Link>

        {/* Desktop Navigation - Center */}
        <nav className="hidden lg:flex items-center gap-6 text-sm text-white/80">
          {NAV_ITEMS.slice(0, 7).map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={cn(
                  "relative hover:text-white transition py-2",
                  active && "text-white"
                )}
              >
                {item.label}
                {active && (
                  <span className="absolute left-0 -bottom-0.5 h-[2px] w-full bg-[#C9A227]" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Right side - CTA + Mobile menu */}
        <div className="flex items-center gap-3">
          {/* Book Now CTA */}
          <Link
            to="/contact"
            className="rounded-xl bg-[#C9A227] px-5 py-2.5 text-sm font-semibold text-[#111] shadow-[0_8px_24px_rgba(201,162,39,0.25)] hover:-translate-y-0.5 hover:bg-[#d4b03a] transition-all duration-300"
            data-testid="navbar-book-now"
          >
            Book Now
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-white hover:text-[#C9A227] transition"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10">
          <nav className="flex flex-col px-4 py-4">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={(e) => {
                  handleNavClick(e, item.href);
                  if (!item.href.includes('#')) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  "py-3 text-sm transition border-b border-white/10",
                  isActive(item.href)
                    ? "text-[#C9A227]"
                    : "text-white/80 hover:text-white"
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
